/** Instance-owned sprites extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamSprites(runtime) {
  runtime.drawPlanet = function (planet) {
    planet.spin += 0.012;
    const color = '#ffd166';
    const hitRatio = runtime.clamp(planet.hits / planet.maxHits, 0, 1);
    const lowPower = runtime.game.drawQuality === 'low';
    const mediumPower = runtime.game.drawQuality === 'medium';
    runtime.ctx.save();
    runtime.ctx.shadowColor = color;
    runtime.ctx.shadowBlur = lowPower ? 18 : mediumPower ? 30 : 42 + hitRatio * 52;
    const ringCount = lowPower ? 1 : mediumPower ? 2 : 3;
    for (let i = 0; i < ringCount; i += 1) {
      runtime.ctx.save();
      runtime.ctx.translate(planet.x, planet.y);
      runtime.ctx.rotate(planet.spin * (i + 1) + i * 1.7);
      runtime.ctx.strokeStyle =
        i === 0
          ? 'rgba(255,248,218,0.74)'
          : i === 1
            ? 'rgba(255,209,102,0.58)'
            : 'rgba(184,98,32,0.48)';
      runtime.ctx.lineWidth = 2.5 + i;
      runtime.ctx.shadowColor = runtime.ctx.strokeStyle;
      runtime.ctx.shadowBlur = 18;
      runtime.ctx.beginPath();
      runtime.ctx.ellipse(
        0,
        0,
        planet.r + 12 + i * 7,
        planet.r * (0.34 + i * 0.12),
        0,
        Math.PI * 0.12,
        Math.PI * (1.22 + i * 0.12),
      );
      runtime.ctx.stroke();
      runtime.ctx.restore();
    }
    const coreSprite = runtime.getPlanetCoreSprite(Math.round(planet.r));
    runtime.ctx.drawImage(
      coreSprite,
      planet.x - coreSprite.width / 2,
      planet.y - coreSprite.height / 2,
    );
    if (!lowPower) {
      runtime.ctx.save();
      runtime.ctx.translate(planet.x, planet.y);
      runtime.ctx.rotate(-planet.spin * 1.8);
      const lavaCount = mediumPower ? 3 : 5;
      for (let i = 0; i < lavaCount; i += 1) {
        runtime.ctx.rotate((Math.PI * 2) / lavaCount);
        runtime.ctx.strokeStyle = 'rgba(255,210,110,0.52)';
        runtime.ctx.lineWidth = 2;
        runtime.ctx.beginPath();
        runtime.ctx.moveTo(-planet.r * 0.5, Math.sin(planet.spin + i) * planet.r * 0.25);
        runtime.ctx.quadraticCurveTo(
          0,
          Math.cos(planet.spin * 1.6 + i) * planet.r * 0.45,
          planet.r * 0.62,
          Math.sin(planet.spin * 1.3 + i) * planet.r * 0.22,
        );
        runtime.ctx.stroke();
      }
      runtime.ctx.restore();
    }
    runtime.ctx.strokeStyle = 'rgba(255,255,255,0.78)';
    runtime.ctx.lineWidth = 1.5;
    runtime.ctx.beginPath();
    runtime.ctx.arc(
      planet.x - planet.r * 0.16,
      planet.y - planet.r * 0.22,
      planet.r * 0.28,
      0,
      Math.PI * 2,
    );
    runtime.ctx.stroke();
    runtime.ctx.strokeStyle = 'rgba(255,255,255,0.42)';
    runtime.ctx.lineWidth = 3;
    runtime.ctx.beginPath();
    runtime.ctx.ellipse(
      planet.x,
      planet.y,
      planet.r + 9,
      planet.r * 0.44,
      planet.spin,
      0,
      Math.PI * 2,
    );
    runtime.ctx.stroke();
    runtime.ctx.strokeStyle = '#ffd166';
    runtime.ctx.lineWidth = 4;
    runtime.ctx.beginPath();
    runtime.ctx.arc(
      planet.x,
      planet.y,
      planet.r + 6,
      -Math.PI / 2,
      -Math.PI / 2 + Math.PI * 2 * hitRatio,
    );
    runtime.ctx.stroke();
    runtime.ctx.fillStyle = 'rgba(5,7,18,0.72)';
    runtime.ctx.font = '900 10px Sora, Inter, sans-serif';
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(String(planet.maxHits - planet.hits), planet.x, planet.y + 0.5);
    runtime.ctx.restore();
  };
  runtime.getPlanetCoreSprite = function (radius) {
    if (runtime.planetSpriteCache.has(radius)) return runtime.planetSpriteCache.get(radius);
    const padding = 10;
    const size = radius * 2 + padding * 2;
    const sprite = document.createElement('canvas');
    sprite.width = size;
    sprite.height = size;
    const target = sprite.getContext('2d');
    const cx = size / 2;
    const cy = size / 2;
    const core = target.createRadialGradient(
      cx - radius * 0.22,
      cy - radius * 0.28,
      1,
      cx,
      cy,
      radius,
    );
    core.addColorStop(0, '#ffffff');
    core.addColorStop(0.16, '#fff3bd');
    core.addColorStop(0.38, '#ffd166');
    core.addColorStop(0.72, '#b85f1c');
    core.addColorStop(1, 'rgba(8,3,18,0.95)');
    target.beginPath();
    target.arc(cx, cy, radius, 0, Math.PI * 2);
    target.fillStyle = core;
    target.fill();
    target.strokeStyle = 'rgba(255,255,255,0.78)';
    target.lineWidth = 1.5;
    target.beginPath();
    target.arc(cx - radius * 0.16, cy - radius * 0.22, radius * 0.28, 0, Math.PI * 2);
    target.stroke();
    runtime.planetSpriteCache.set(radius, sprite);
    return sprite;
  };
  runtime.drawOrb = function (orb) {
    orb.pulse += 0.08;
    const color = runtime.orbColor(orb.type);
    runtime.ctx.save();
    runtime.ctx.shadowColor = color;
    runtime.ctx.shadowBlur = 24;
    runtime.ctx.beginPath();
    runtime.ctx.arc(orb.x, orb.y, orb.r + Math.sin(orb.pulse) * 2, 0, Math.PI * 2);
    runtime.ctx.fillStyle = color;
    runtime.ctx.fill();
    runtime.ctx.fillStyle = 'rgba(5,7,18,0.68)';
    runtime.ctx.font = '900 16px Sora, Inter, sans-serif';
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(runtime.orbLabel(orb.type), orb.x, orb.y + 1);
    runtime.ctx.restore();
  };
  runtime.drawWall = function (wall) {
    const durability = runtime.clamp(wall.hp / Math.max(1, wall.maxHp), 0, 1);
    wall.heat = Math.max(0, wall.heat - 0.06);
    runtime.ctx.save();
    runtime.ctx.translate(wall.x + wall.w / 2, wall.y + wall.h / 2);
    runtime.ctx.rotate(wall.angle);
    runtime.ctx.shadowColor = wall.tunnel ? '#ffd166' : '#b86b2d';
    runtime.ctx.shadowBlur = (wall.tunnel ? 18 : 12) + wall.heat * 26;
    runtime.roundRect(-wall.w / 2, -wall.h / 2, wall.w, wall.h, 6);
    const rail = runtime.ctx.createLinearGradient(-wall.w / 2, 0, wall.w / 2, 0);
    rail.addColorStop(0, `rgba(80,35,7,${0.72 + durability * 0.16})`);
    rail.addColorStop(0.22, `rgba(255,226,145,${0.24 + durability * 0.38})`);
    rail.addColorStop(
      0.5,
      wall.tunnel
        ? `rgba(191,127,48,${0.42 + durability * 0.38})`
        : `rgba(160,82,28,${0.38 + durability * 0.34})`,
    );
    rail.addColorStop(0.82, `rgba(255,244,204,${0.18 + durability * 0.24})`);
    rail.addColorStop(1, `rgba(64,25,5,${0.76 + durability * 0.14})`);
    runtime.ctx.fillStyle = rail;
    runtime.ctx.fill();
    runtime.ctx.strokeStyle = 'rgba(255,238,180,0.6)';
    runtime.ctx.lineWidth = 1.2;
    runtime.ctx.stroke();
    if (wall.tunnel) {
      runtime.ctx.fillStyle = 'rgba(255,248,218,0.34)';
      runtime.roundRect(-wall.w / 2 + 8, -2, (wall.w - 16) * durability, 4, 3);
      runtime.ctx.fill();
    }
    if (wall.w > 52) {
      runtime.ctx.fillStyle = 'rgba(5,7,18,0.68)';
      runtime.ctx.font = '900 13px Sora, Inter, sans-serif';
      runtime.ctx.textAlign = 'center';
      runtime.ctx.textBaseline = 'middle';
      runtime.ctx.fillText(wall.hp, 0, 0.5);
    }
    runtime.ctx.restore();
  };
  runtime.prepareTurretSprite = function () {
    if (
      !runtime.turretSpriteCtx ||
      !runtime.turretImage.complete ||
      !runtime.turretImage.naturalWidth
    )
      return;
    runtime.turretSpriteCtx.clearRect(
      0,
      0,
      runtime.turretSprite.width,
      runtime.turretSprite.height,
    );
    runtime.turretSpriteCtx.drawImage(
      runtime.turretImage,
      0,
      0,
      runtime.turretSprite.width,
      runtime.turretSprite.height,
    );
    const imageData = runtime.turretSpriteCtx.getImageData(
      0,
      0,
      runtime.turretSprite.width,
      runtime.turretSprite.height,
    );
    const data = imageData.data;
    const key = { r: data[0], g: data[1], b: data[2] };
    for (let i = 0; i < data.length; i += 4) {
      const dr = data[i] - key.r;
      const dg = data[i + 1] - key.g;
      const db = data[i + 2] - key.b;
      const distance = Math.sqrt(dr * dr + dg * dg + db * db);
      if (distance < 86 && data[i + 1] > data[i] * 1.35 && data[i + 1] > data[i + 2] * 1.35) {
        const alpha = runtime.clamp((distance - 22) / 64, 0, 1);
        data[i + 3] = Math.round(data[i + 3] * alpha);
        if (alpha < 0.8) {
          data[i] = Math.min(255, data[i] * 1.05 + 20);
          data[i + 1] = Math.max(0, data[i + 1] * 0.45);
          data[i + 2] = Math.min(255, data[i + 2] * 0.82 + 10);
        }
      }
    }
    runtime.turretSpriteCtx.putImageData(imageData, 0, 0);
    runtime.turretReady = true;
  };
  runtime.drawLauncher = function () {
    const aim = runtime.trigDeg(runtime.game.angle);
    const radians = aim.radians;
    const muzzleX = runtime.launcher.x + aim.cos * runtime.LAUNCHER_MUZZLE_DISTANCE;
    const muzzleY = runtime.launcher.y - aim.sin * runtime.LAUNCHER_MUZZLE_DISTANCE;
    if (runtime.turretReady) {
      const size = runtime.LAUNCHER_SPRITE_SIZE;
      runtime.ctx.save();
      runtime.ctx.translate(runtime.launcher.x, runtime.launcher.y);
      runtime.ctx.shadowColor = '#ffd166';
      runtime.ctx.shadowBlur = 18 + runtime.game.launchFlash * 30;
      runtime.ctx.fillStyle = 'rgba(19,8,2,0.72)';
      runtime.ctx.beginPath();
      runtime.ctx.ellipse(0, 27, 108, 30, 0, 0, Math.PI * 2);
      runtime.ctx.fill();
      runtime.ctx.rotate(Math.PI / 2 - radians);
      runtime.ctx.drawImage(runtime.turretSprite, -size / 2, -size / 2, size, size);
      runtime.ctx.globalAlpha = 0.32 + runtime.game.launchFlash * 0.38;
      runtime.ctx.globalCompositeOperation = 'lighter';
      runtime.ctx.fillStyle = '#ffd166';
      runtime.ctx.beginPath();
      runtime.ctx.arc(0, -13, 24, 0, Math.PI * 2);
      runtime.ctx.fill();
      runtime.ctx.restore();
    } else {
      runtime.ctx.save();
      runtime.ctx.translate(runtime.launcher.x, runtime.launcher.y);
      runtime.ctx.shadowColor = '#ffd166';
      runtime.ctx.shadowBlur = 22 + runtime.game.launchFlash * 28;
      const base = runtime.ctx.createRadialGradient(-14, -12, 2, 0, 0, 34);
      base.addColorStop(0, '#fff1b4');
      base.addColorStop(0.28, '#f5a642');
      base.addColorStop(0.64, '#8e4311');
      base.addColorStop(1, '#281005');
      runtime.ctx.beginPath();
      runtime.ctx.arc(0, 0, 28, 0, Math.PI * 2);
      runtime.ctx.fillStyle = base;
      runtime.ctx.fill();
      runtime.ctx.strokeStyle = 'rgba(255,238,180,0.78)';
      runtime.ctx.lineWidth = 3;
      runtime.ctx.stroke();
      runtime.ctx.restore();
    }
    if (runtime.game.launchFlash > 0) {
      runtime.ctx.save();
      runtime.ctx.globalAlpha = runtime.game.launchFlash / 0.38;
      runtime.ctx.strokeStyle = '#fff4c2';
      runtime.ctx.shadowColor = '#ffd166';
      runtime.ctx.shadowBlur = 34;
      runtime.ctx.lineWidth = 14;
      runtime.ctx.beginPath();
      runtime.ctx.moveTo(muzzleX, muzzleY);
      runtime.ctx.lineTo(muzzleX + aim.cos * 175, muzzleY - aim.sin * 175);
      runtime.ctx.stroke();
      runtime.ctx.restore();
    }
  };
  runtime.drawLaunchHud = function () {
    const now = runtime.now();
    const seconds = now / 1000;
    runtime.drawLaunchCountdownPanel(
      runtime.W - 170,
      runtime.LAUNCH_HUD_Y,
      runtime.getLaunchCountdownSeconds(now),
    );
    runtime.drawNeutronFuelTankPanel(170, runtime.LAUNCH_HUD_Y, seconds);
  };
  runtime.drawLaunchCountdownPanel = function (cx, cy, waitLeft) {
    const modeConfig = runtime.currentModeConfig();
    let label = 'NEXT LAUNCH';
    let value = waitLeft.toFixed(3);
    let ready = waitLeft <= 0;
    if (modeConfig.durationSeconds) {
      label = 'TIME LEFT';
      value = runtime.formatClock(runtime.game.modeTimerRemaining);
      ready = runtime.game.modeTimerRemaining > 10;
    } else if (modeConfig.timedRowAdvanceSeconds) {
      label = 'NEXT ROW';
      value = `${Math.max(0, runtime.game.timedCurrentRowAdvanceSeconds - runtime.game.timedRowAdvanceElapsed).toFixed(1)}s`;
      ready = false;
    }
    runtime.ctx.save();
    runtime.ctx.globalAlpha = 0.64;
    runtime.ctx.shadowColor = 'rgba(255, 209, 102, 0.38)';
    runtime.ctx.shadowBlur = 12;
    runtime.ctx.strokeStyle = 'rgba(255, 224, 150, 0.34)';
    runtime.ctx.fillStyle = 'rgba(20, 8, 2, 0.36)';
    runtime.roundRectCtx(runtime.ctx, cx - 86, cy - 31, 172, 62, 14);
    runtime.ctx.fill();
    runtime.ctx.stroke();
    runtime.ctx.globalAlpha = 0.76;
    runtime.ctx.fillStyle = 'rgba(255, 241, 188, 0.72)';
    runtime.ctx.font = '900 10px Orbitron, Arial, sans-serif';
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(label, cx, cy - 16);
    runtime.ctx.font = "900 25px 'Courier New', monospace";
    runtime.ctx.fillStyle = ready ? 'rgba(116, 255, 141, 0.76)' : 'rgba(255, 209, 102, 0.82)';
    runtime.ctx.fillText(value, cx, cy + 8);
    runtime.ctx.restore();
  };
}
